import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Sparkles, Loader2 } from 'lucide-react';
import * as api from '../lib/api';
import posthog from '../lib/posthog';
import { trackEvent } from '../lib/metaPixel';

/* Card form for Sirati+ (must be rendered inside <Elements>).
   Monthly and yearly create a subscription; lifetime is a one-time payment. */
const PaymentForm = ({ priceId, tier, userId, onSuccess, buttonLabel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const succeed = (paymentId) => {
    posthog.capture('premium_upgraded', { plan: tier });
    trackEvent('Purchase', { plan: tier, eventId: paymentId ? `purchase_${paymentId}` : undefined });
    onSuccess();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!priceId) {
      setError('This plan is not available right now. Please try another plan or come back soon.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }

      const res = await api.createStripeSubscription({
        user_id: userId,
        price_id: priceId,
        payment_method_id: paymentMethod.id,
        referral: window.Rewardful?.referral || null,
      });

      const { client_secret, status, kind, payment_intent_id, subscription_id } = res.data;
      const paymentId = payment_intent_id || subscription_id;

      if (status === 'active' || status === 'succeeded') {
        succeed(paymentId);
        return;
      }

      if (client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(client_secret);
        if (confirmError) {
          setError(confirmError.message);
        } else {
          // A one-time payment is only granted once the server has verified it.
          if (kind === 'payment') await api.finalizeLifetime(payment_intent_id);
          succeed(paymentId);
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Payment failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="payment-form">
      <div>
        <label className="text-sm font-medium text-text-main mb-1.5 block" id="card-label">Card details</label>
        <div className="p-4 bg-white rounded-arch border border-primary/20" aria-labelledby="card-label">
          <CardElement options={{
            style: {
              base: { fontSize: '16px', color: '#1a1a2e', '::placeholder': { color: '#999' } },
              invalid: { color: '#ef4444' },
            },
          }} />
        </div>
      </div>
      {error && <p className="text-sm text-red-500" role="alert" data-testid="payment-error">{error}</p>}
      <button type="submit" disabled={!stripe || loading}
        className="w-full min-h-[48px] py-3 bg-primary text-white rounded-full text-base font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="pay-btn">
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Processing...</> : <><Sparkles className="w-5 h-5" aria-hidden="true" /> {buttonLabel || 'Start My Sirati+ Journey'}</>}
      </button>
    </form>
  );
};

export default PaymentForm;
